import React, { FC } from 'react';
import styled from 'styled-components';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useForm from '../../../../hooks/useForm';
import { loginFormValidator, registrationFormValidator } from '../../../../hooks/useForm/validators';
import { ValidationErrors } from '../../../../redux/user/types';
import useTranslator from '../../../../hooks/useTranslator';
import useConfig from '../../../../hooks/useConfig';
import { MIN_PASSWORD_LENGTH } from '../../../../hooks/useForm/loginFormValidator';
import TextInput from '../../../Generic/FormElements/index';
import useFeatureToggle from '../../../../hooks/useFeatureToggle';
import { BellaTestIds, VictorQaClasses } from '../../../../../common/qaIds';
import StyledInputWrapper from './StyledInputWrapper';
import StyledCheckboxLabel from './StyledCheckboxLabel';
import StyledCheckbox from './StyledCheckbox';
import StyledCheckboxText from './StyledCheckboxText';
import StyledFormError from './StyledFormError';
import ForgotPasswordLink from './ForgotPasswordLink';
import FacebookLoginSection from './FacebookLoginSection';

interface FormProps {
    isRegisterForm: boolean;
    handleFormSubmit: Function;
    validationErrors: ValidationErrors;
    handleFacebookLogin: (event: React.MouseEvent<HTMLButtonElement>) => void;
    isFacebookLoaded?: boolean;
    isFetching: boolean;
    partialLoginEmail: string;
}

const StyledActionButton = styled(ActionButton)`
    max-width: 300px;
`;

export const StyledRegisterFormCheckBoxes = styled.div``;

// eslint-disable-next-line complexity
const Form: FC<FormProps> = ({
    handleFormSubmit,
    handleFacebookLogin,
    isRegisterForm,
    validationErrors,
    partialLoginEmail,
    isFetching,
    isFacebookLoaded = true,
}) => {
    const t = useTranslator();
    const featureToggles = useFeatureToggle();
    const {
        links: { agb, dataProtection, creditCheckPolicy, forgotPassword },
    } = useConfig();
    const initialFormValues = {
        email: partialLoginEmail || '',
        password: '',
        isNewsletterSubscribed: false,
        isTermsAccepted: false,
    };

    const validator = isRegisterForm ? registrationFormValidator : loginFormValidator;

    const { values, errors, onChange, onBlur, onSubmit, onBlurValidateFacebookOnly } = useForm(
        () => handleFormSubmit(values),
        validator,
        validationErrors,
        initialFormValues
    );

    const hasTermsError = errors.isTermsAccepted || false;
    const translatedPasswordText = t('Password');
    return (
        <form onSubmit={onSubmit} noValidate>
            <StyledInputWrapper>
                <TextInput
                    inputType="roundedCornersInputs"
                    autoComplete="email"
                    value={values.email}
                    name="email"
                    placeholder={t('Email')}
                    onChange={onChange}
                    onBlur={featureToggles.isFacebookLoginDesktopEnabled ? onBlur : onBlurValidateFacebookOnly}
                    type="email"
                    data-testid={BellaTestIds.loginRegisterEmailField}
                    required
                    className={partialLoginEmail ? VictorQaClasses.partialLoginOverlayEmail : ''}
                />
                {errors.email && (
                    <StyledFormError
                        dangerouslySetInnerHTML={{
                            __html: t(errors.email),
                        }}
                    />
                )}
            </StyledInputWrapper>
            <StyledInputWrapper>
                <TextInput
                    inputType="roundedCornersInputs"
                    autoComplete="password"
                    value={values.password}
                    type="password"
                    name="password"
                    placeholder={t('Password')}
                    onChange={onChange}
                    onBlur={onBlur}
                    data-testid={BellaTestIds.loginRegisterPasswordField}
                    required
                    className={partialLoginEmail ? VictorQaClasses.partialLoginOverlayPassword : ''}
                />
                {errors.password && (
                    <StyledFormError
                        dangerouslySetInnerHTML={{
                            __html: t(errors.password, [translatedPasswordText, MIN_PASSWORD_LENGTH]),
                        }}
                    />
                )}
            </StyledInputWrapper>
            {!isRegisterForm && <ForgotPasswordLink linkText={t('Forgot password?')} href={forgotPassword} />}
            {isRegisterForm && (
                <StyledRegisterFormCheckBoxes>
                    <StyledCheckboxLabel>
                        <StyledCheckbox
                            checked={values.isNewsletterSubscribed}
                            name="isNewsletterSubscribed"
                            handleInputChange={onChange}
                        />
                        <StyledCheckboxText hasError={false}>{t('Yes, I want to get newsletter')}</StyledCheckboxText>
                    </StyledCheckboxLabel>
                    <StyledCheckboxLabel>
                        <StyledCheckbox
                            hasError={hasTermsError}
                            checked={values.isTermsAccepted}
                            name="isTermsAccepted"
                            handleInputChange={onChange}
                        />
                        <StyledCheckboxText
                            hasError={hasTermsError}
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'By clicking "Register Now" I confirm that I have read%sAGB%s, %sData-policy%s and %sCredit Check Policy%s',
                                    [
                                        `<a href="${agb}" target="_blank">`,
                                        '</a>',
                                        `<a href="${dataProtection}" target="_blank">`,
                                        '</a>',
                                        `<a href="${creditCheckPolicy}" target="_blank">`,
                                        '</a>',
                                    ]
                                ),
                            }}
                        />
                    </StyledCheckboxLabel>
                </StyledRegisterFormCheckBoxes>
            )}
            <StyledActionButton
                data-testid={BellaTestIds.loginRegSubmitBtn}
                kind="primary"
                type="submit"
                disabled={isFetching}
                className={partialLoginEmail ? VictorQaClasses.partialLoginOverlaySubmit : ''}
            >
                {isRegisterForm ? t('Register') : t('Login')}
            </StyledActionButton>
            <FacebookLoginSection
                isRegisterForm={isRegisterForm}
                handleFacebookLogin={handleFacebookLogin}
                isFacebookLoaded={isFacebookLoaded}
                isFacebookLoginDesktopEnabled={featureToggles.isFacebookLoginDesktopEnabled}
            />
        </form>
    );
};

export default Form;
